import React, { useContext, useEffect, useRef, useState } from "react";
import { Search, UserCog } from "lucide-react";
import { useTranslation } from "../../context/Internationalization";
import { AuthContext } from "../../context/AuthContext";
import {
  GET_USER_PROFILES_SA,
  SEARCH_USERS_FOR_IMPERSONATION_SA,
} from "./SuperAdminService";
import PageHeader from "../../Components/Common/PageHeader";
import { Button } from "../../shadcn/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../shadcn/components/ui/accordion";
import TextFieldRenderer from "../../Components/Form/TextFieldRenderer";
import { GET_USER } from "../../graphql/userGraphql";
import ImpersonateConfirmationModal from "./UsersTable/ImpersonateConfirmationModal";
import { useSuperAdmin } from "../../context/SuperAdminContext";
import { getStorage } from "../../auth/utils";
import { useNavigate } from "react-router-dom";
import Form from "../../Components/Form/Form";
import ProfilesTable from "./ProfilesTable/ProfilesTable";
import TeamsTable from "./TeamsTable/TeamsTable";
import UserInvitationsTable from "./UserInvitations/UserInvitationsTable";
import UserClubTable from "./UserClubTable/UserClubTable";
import useGraphql from "../../Hooks/useGraphql";
import useLog from "../../Hooks/useLog";
import { USER_ROLES } from "../../utility/Enum";
import {
  endImpersonationSession,
  setImpersonatedActiveClub,
  suspendActiveClubSelection,
} from "../../auth/impersonationClub";
import { Item, ItemActions, ItemContent, ItemDescription, ItemGroup, ItemTitle } from "../../shadcn/components/ui/item";
import { toast } from "sonner";

const schema = (t) => [
  {
    name: "firstName",
    label: t("firstName"),
    disabled: true,
    renderer: TextFieldRenderer,
    row: 1,
  },
  {
    name: "lastName",
    label: t("lastName"),
    disabled: true,
    renderer: TextFieldRenderer,
    row: 1,
  },
  {
    name: "mobile",
    label: t("mobile"),
    disabled: true,
    renderer: TextFieldRenderer,
    row: 1,
  },
  {
    name: "email",
    label: t("email"),
    disabled: true,
    renderer: TextFieldRenderer,
    row: 1,
  },
];

const initialState = {
  user: null,
  candidates: [],
  profiles: [],
  loading: false,
  impersonating: false,
  impersonateModal: false,
};

const SuperAdminImpersonate = (props) => {
  const { t, switchLanguage } = useTranslation();
  const [state, setState] = useState(initialState);
  const [filters, setFilters] = useState({ id: "", identity: "" });
  const { setImpersonate } = useContext(AuthContext);
  const { setUser } = useSuperAdmin();
  const navigate = useNavigate();
  const { axiosGraphQL } = useGraphql();
  const { errorLog } = useLog();
  const requestVersion = useRef(0);
  const impersonationPending = useRef(false);
  useEffect(() => () => { requestVersion.current += 1; }, []);

  const handleImpersonate = () => {
    setState({ ...state, impersonateModal: true });
  };

  const reportError = (error, operation, query = "SEARCH_USERS_FOR_IMPERSONATION_SA") => {
    errorLog(error, "SuperAdminImpersonate", operation, query);
    toast.error(error.message);
  };

  const loadSelectedUser = async (user, version) => {
    if (!user?._id) throw new Error(t("impersonation_missing_id"));
    const response = await axiosGraphQL({
      query: GET_USER_PROFILES_SA,
      variables: { userId: user._id },
    });
    if (version !== requestVersion.current) return;
    setState((previous) => ({
      ...previous,
      user,
      profiles: response?.data?.data?.getClubProfilesSA || [],
      loading: false,
    }));
  };

  const selectCandidate = async (user) => {
    if (impersonationPending.current) return;
    const version = ++requestVersion.current;
    setState((previous) => ({ ...previous, user: null, profiles: [], loading: true }));
    try {
      await loadSelectedUser(user, version);
    } catch (error) {
      if (version !== requestVersion.current) return;
      setState((previous) => ({ ...previous, loading: false }));
      reportError(error, "selectCandidate", "GET_USER_PROFILES_SA");
    }
  };

  const resolveUser = async () => {
    if (impersonationPending.current) return;
    const version = ++requestVersion.current;
    setState({ ...initialState, loading: true });
    const userId = filters.id.trim();
    const identity = filters.identity.trim();
    if (!userId && !identity) {
      setState(initialState);
      return;
    }
    try {
      const response = await axiosGraphQL({
        query: SEARCH_USERS_FOR_IMPERSONATION_SA,
        variables: userId ? { userId } : { identity },
      });
      if (version !== requestVersion.current) return;
      const candidates = response?.data?.data?.searchUsersForImpersonationSA || [];
      if (userId && (candidates.length !== 1 || candidates[0]._id !== userId.toLowerCase())) {
        throw new Error(t("impersonation_identity_mismatch"));
      }
      if (!candidates.length) throw new Error(t("impersonation_no_matches"));
      setState({ ...initialState, candidates, loading: candidates.length === 1 });
      if (candidates.length === 1) await loadSelectedUser(candidates[0], version);
    } catch (error) {
      if (version !== requestVersion.current) return;
      setState((previous) => ({ ...previous, loading: false }));
      reportError(error, "resolveUser");
    }
  };

  const onChangeFilter = ({ target: { name, value } }) => {
    if (impersonationPending.current) return;
    requestVersion.current += 1;
    setState(initialState);
    // Only one lookup mode is active; an ID is never translated into a phone lookup.
    setFilters({ id: "", identity: "", [name]: value });
  };

  const onImpersonate = async () => {
    if (impersonationPending.current || state.loading || !state.user?._id) return;
    const target = state.user;
    impersonationPending.current = true;
    setState((previous) => ({ ...previous, impersonating: true, impersonateModal: false }));
    const storage = getStorage();
    suspendActiveClubSelection(storage);
    try {
      setUser(target);
      const response = await axiosGraphQL({ query: GET_USER });
      const me = response?.data?.data?.me;
      if (me?.user?._id !== target._id) {
        throw new Error(t("impersonation_identity_mismatch"));
      }
      if (!me.club || !me.clubs?.length) {
        throw new Error(t("impersonation_no_club"));
      }

      const user = { ...me.user, club: me.club, clubs: me.clubs };
      if (user.language !== storage.getItem("language")) {
        switchLanguage(user.language);
      }
      setImpersonatedActiveClub(storage, user.club._id);
      storage.setItem("impersonate", JSON.stringify(user));
      setImpersonate(user);
      navigate("/");
    } catch (error) {
      endImpersonationSession(storage);
      setUser(null);
      setImpersonate(null);
      reportError(error, "onImpersonate", "GET_USER");
    } finally {
      impersonationPending.current = false;
      setState((previous) => ({ ...previous, impersonating: false }));
    }
  };

  const onCloseModal = () => {
    setState({ ...state, impersonateModal: false });
  };

  const sections = [
    {
      id: "clubs",
      title: t("clubs"),
      content: (
        <UserClubTable
          data={state.user?.clubs || []}
          emptyMessage={t("no_clubs_found")}
        />
      ),
    },
    {
      id: "teams",
      title: t("teams"),
      content: (
        <TeamsTable
          data={
            state?.user?.teams?.map((team) => ({
              ...team,
              userId: state.user?._id,
            })) || []
          }
          emptyMessage={t("no_teams_found")}
        />
      ),
    },
    {
      id: "invitations",
      title: t("user_invitations"),
      content: (
        <UserInvitationsTable
          data={state?.user?.invitations || []}
          emptyMessage={t("no_notifications_found")}
        />
      ),
    },
    {
      id: "profiles",
      title: t("profiles"),
      content: (
        <ProfilesTable
          data={state.profiles ? state.profiles : []}
          emptyMessage={t("no_players_found")}
        />
      ),
    },
  ];

  return (
    <main className="flex-1 p-6">
      <ImpersonateConfirmationModal
        open={state.impersonateModal}
        onClose={onCloseModal}
        onImpersonate={onImpersonate}
      />

      <PageHeader title={t("search_user")} />
      <div className="grid grid-cols-1 items-end gap-4 py-5 min-[600px]:grid-cols-[1fr_1fr_auto]">
        <TextFieldRenderer
          label={"Id"}
          onChange={onChangeFilter}
          name={"id"}
          value={filters.id}
          disabled={state.impersonating}
        />
        <TextFieldRenderer
          label={t("email_or_mobile")}
          onChange={onChangeFilter}
          name={"identity"}
          value={filters.identity}
          disabled={state.impersonating}
        />
        <Button
          type="button"
          onClick={resolveUser}
          disabled={state.loading || state.impersonating || (!filters.id.trim() && !filters.identity.trim())}
        >
          <Search aria-hidden="true" />
          {t("search_user")}
        </Button>
      </div>

      {state.candidates.length > 1 && (
        <div className="my-4" role="group" aria-label={t("impersonation_choose_account")}>
          <p className="mb-2">{t("impersonation_choose_account")}</p>
          <ItemGroup>
            {state.candidates.map((candidate) => (
              <Item key={candidate._id} role="listitem" variant="outline" className="[overflow-wrap:anywhere]">
                <ItemContent className="min-w-0">
                  <ItemTitle>{candidate.firstName} {candidate.lastName}</ItemTitle>
                  <ItemDescription className="line-clamp-none">
                    Id: {candidate._id}<br />
                    {candidate.mobile || "—"} · {candidate.email || "—"}<br />
                    {t("clubs")}: {candidate.clubs?.map((club) => club.name).join(", ") || t("no_clubs_found")}<br />
                    {t("teams")}: {candidate.teams?.map((team) => [team.club?.name, team.name].filter(Boolean).join(" / ")).join(", ") || t("no_teams_found")}
                  </ItemDescription>
                </ItemContent>
                <ItemActions>
                  <Button
                    type="button"
                    variant={state.user?._id === candidate._id ? "secondary" : "outline"}
                    className="h-auto min-h-9 whitespace-normal py-2"
                    onClick={() => selectCandidate(candidate)}
                    disabled={state.loading || state.impersonating}
                    aria-pressed={state.user?._id === candidate._id}
                  >
                    {t("impersonation_select_account")}
                  </Button>
                </ItemActions>
              </Item>
            ))}
          </ItemGroup>
        </div>
      )}

      {state.user && (
        <div className="flex flex-col gap-4">
          <div className="flex justify-end">
            <Button
              type="button"
              onClick={handleImpersonate}
              disabled={state.loading || state.impersonating || !state.user?.roles?.includes(USER_ROLES.CLUB_ADMIN)}
            >
              <UserCog aria-hidden="true" />
              Impersonate
            </Button>
          </div>
          <Form schema={schema(t)} values={state.user || {}} />
          <Accordion multiple>
            {sections.map((section) => (
              <AccordionItem key={section.id} value={section.id}>
                <AccordionTrigger className="focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/30 focus-visible:ring-inset">
                  {section.title}
                </AccordionTrigger>
                {/* Keep the tables mounted, like the old panels. */}
                <AccordionContent keepMounted>{section.content}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      )}
    </main>
  );
};

SuperAdminImpersonate.propTypes = {};

export default SuperAdminImpersonate;
